import { useLayoutEffect, useRef, useState } from "react";
import {
  createProgressRepository,
  isStorageAvailable,
} from "@/infrastructure/persistence/progress-repository-factory";
import { useProgressStore } from "../stores/progressStore";

export interface ProgressBootstrapResult {
  /** 進捗データが壊れており初期Progressへ復旧した（E-6）。呼び出し側が通知（O-4）を出すためのシグナル。 */
  readonly recoveryError: boolean;
  /** localStorageが使用不可でメモリ実装にフォールバックした（E-7）。同上。 */
  readonly storageUnavailable: boolean;
}

const INITIAL_RESULT: ProgressBootstrapResult = { recoveryError: false, storageUnavailable: false };

/**
 * 起動時に一度だけ`Progress`をリポジトリから読み込み、`progressStore`へ反映する。
 * `useLayoutEffect`を使い、ブラウザが描画する前（コミット直後・ペイント前）に同期的に
 * 読み込みを終わらせることで、初期状態（空のProgress）が一瞬でも見える「ちらつき」を防ぐ
 * （`Progress`自体はlocalStorageから同期的に読めるため、進捗サマリ・CTAは即時描画できる
 * — `screen-flow.md` §8.1「初期描画」）。
 *
 * 戻り値のE-6/E-7シグナルはこのフック自身では通知しない（`useOverlay`はプレゼンテーション層に
 * 属し、アプリケーション層のこのフックから直接使うとレイヤー依存が逆転するため）。
 * 呼び出し側（`AppShell`）が読み取って触れ（O-4）を出す。
 */
export function useProgressBootstrap(): ProgressBootstrapResult {
  const setProgress = useProgressStore((state) => state.setProgress);
  const bootstrapped = useRef(false);
  const [result, setResult] = useState<ProgressBootstrapResult>(INITIAL_RESULT);

  useLayoutEffect(() => {
    if (bootstrapped.current) {
      return;
    }
    bootstrapped.current = true;
    const storageUnavailable = !isStorageAvailable(window.localStorage);
    const repository = createProgressRepository();
    setProgress(repository.load());
    setResult({ recoveryError: repository.hadRecoveryError(), storageUnavailable });
  }, [setProgress]);

  return result;
}
